import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin, map, of } from 'rxjs';

import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { Message } from '@openng/optimus-ui/message';
import { TableModule } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import { CurrencyFormatPipe, rxResourceFixed } from '@zayan-erp/shared';

import { DsmPackageDetail, DsmPackageListItem, DsmPackageProductSummary } from '../../../models/dsm-package.models';
import { DsmCurrencyService } from '../../../services/dsm-currency.service';
import {
  DsmProductAttributeGroup,
  DsmProductOption,
  DsmSelectedProductEntry,
  DsmSelectedProductValues
} from '../../../models/dsm-deal-options.models';
import { DsmModelElevationProductsService } from '../../../services/dsm-model-elevation-products.service';
import { DsmPackageService } from '../../../services/dsm-package.service';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmSelectedProductPanelComponent } from '../dsm-selected-product-panel/dsm-selected-product-panel.component';
import { DsmSelectedProductConfigFormComponent } from '../dsm-selected-product-config-form/dsm-selected-product-config-form.component';

@Component({
  selector: 'dsm-add-option-packages-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    Message,
    TableModule,
    TagModule,
    TooltipModule,
    CurrencyFormatPipe,
    DsmSelectedProductPanelComponent,
    DsmSelectedProductConfigFormComponent
  ],
  templateUrl: './dsm-add-option-packages-tab.component.html'
})
export class DsmAddOptionPackagesTabComponent {
  readonly #packageService = inject(DsmPackageService);
  readonly #productsService = inject(DsmModelElevationProductsService);
  readonly #dealContext = inject(DsmApsDealContextService);
  readonly #currencyService = inject(DsmCurrencyService);
  readonly #destroyRef = inject(DestroyRef);

  readonly currencySymbol = computed(() => this.#currencyService.currencySymbol());

  readonly modelId = input.required<string>();
  readonly elevationId = input.required<string>();
  readonly addendumId = input.required<string>();
  readonly addedProductIds = input.required<string[]>();

  readonly packageEntriesAdded = output<DsmSelectedProductEntry[]>();

  readonly configFormRef = viewChild(DsmSelectedProductConfigFormComponent);

  readonly packagesResource = rxResourceFixed({
    params: () => ({
      modelId: this.modelId(),
      elevationId: this.elevationId(),
      projectId: this.#dealContext.currentDeal()?.project?.id
    }),
    defaultValue: [] as DsmPackageListItem[],
    stream: ({ params }) => {
      if (!params.projectId || !params.modelId || !params.elevationId) {
        return of([] as DsmPackageListItem[]);
      }
      return this.#packageService.getPackages(
        {
          projectId: params.projectId,
          modelId: params.modelId,
          elevationId: params.elevationId,
          isActive: true
        },
        false
      );
    }
  });

  readonly packages = computed(() => this.packagesResource.value() ?? []);
  readonly packagesHasError = computed(() => !!this.packagesResource.error());

  readonly activePackageId = signal<string | null>(null);
  readonly packageDialogVisible = computed(() => this.activePackageId() !== null);

  readonly packageDetailResource = rxResourceFixed({
    params: () => ({ id: this.activePackageId() }),
    defaultValue: null as DsmPackageDetail | null,
    stream: ({ params }) => {
      if (!params.id) return of(null as DsmPackageDetail | null);
      return this.#packageService.getPackageById(params.id, false);
    }
  });

  readonly packageDetail = computed(() => this.packageDetailResource.value());
  readonly packageDetailHasError = computed(() => !!this.packageDetailResource.error());

  readonly workingEntries = signal<DsmSelectedProductEntry[]>([]);
  readonly attributesByProductId = signal<Map<string, DsmProductAttributeGroup[]>>(new Map());
  readonly attributesLoaded = signal(true);
  readonly attributesLoadFailed = signal(false);
  readonly attributesReloadTick = signal(0);
  attributesRequestId = 0;

  readonly editingEntryId = signal<string | null>(null);
  readonly editingEntry = computed<DsmSelectedProductEntry | null>(() => {
    const id = this.editingEntryId();
    if (!id) return null;
    return this.workingEntries().find(entry => entry.id === id) ?? null;
  });
  readonly editingProduct = computed<DsmProductOption | null>(() => this.editingEntry()?.product ?? null);
  readonly editingValues = computed<DsmSelectedProductValues | null>(() => this.editingEntry()?.values ?? null);
  readonly editDialogVisible = computed(() => this.editingEntry() !== null);

  readonly missingFieldsByEntryId = computed(() => {
    const map: Record<string, number> = {};
    for (const entry of this.workingEntries()) {
      map[entry.id] = this.countMissingFields(entry);
    }
    return map;
  });

  readonly totalMissingFields = computed(() =>
    this.workingEntries().reduce((sum, entry) => sum + (this.missingFieldsByEntryId()[entry.id] ?? 0), 0)
  );

  readonly unconfigurableCount = computed(
    () => this.workingEntries().filter(entry => this.isEntryUnconfigurable(entry)).length
  );

  readonly packageAddBlocked = computed(
    () =>
      !this.workingEntries().length ||
      !this.attributesLoaded() ||
      this.attributesLoadFailed() ||
      this.totalMissingFields() > 0
  );

  readonly editBlockedByMissingAttributes = computed(() => {
    const product = this.editingProduct();
    if (!product?.selectionRequired) return false;
    return (this.configFormRef()?.productAttributes()?.length ?? 0) === 0;
  });

  readonly packageTotal = computed(() =>
    this.workingEntries().reduce((total, entry) => {
      const amount = (entry.values.unitPrice ?? 0) * (entry.values.quantity ?? 0);
      return total + this.#packageService.amountAfterDiscount(amount, this.packageDiscountPercentage());
    }, 0)
  );

  readonly packageOriginalTotal = computed(() =>
    this.workingEntries().reduce(
      (total, entry) => total + (entry.values.unitPrice ?? 0) * (entry.values.quantity ?? 0),
      0
    )
  );

  readonly packageDiscountPercentage = computed(() => this.packageDetail()?.discountPercentage ?? 0);

  readonly hasPackageDiscount = computed(() => this.packageDiscountPercentage() > 0);

  constructor() {
    effect(() => {
      const detail = this.packageDetail();
      void this.attributesReloadTick();
      const requestId = ++this.attributesRequestId;

      if (!detail) {
        this.workingEntries.set([]);
        this.attributesByProductId.set(new Map());
        this.attributesLoaded.set(true);
        this.attributesLoadFailed.set(false);
        return;
      }

      this.workingEntries.set(
        detail.products.map(product => this.#buildEntry(detail.id, product, detail.discountPercentage ?? 0))
      );

      const isKitByProductId = new Map<string, boolean>();
      for (const p of detail.products) {
        if (p.product?.id) isKitByProductId.set(p.product.id, p.product.productClass?.name === 'Kit');
      }
      const uniqueProductIds = Array.from(isKitByProductId.keys());
      if (uniqueProductIds.length === 0) {
        this.attributesByProductId.set(new Map());
        this.attributesLoaded.set(true);
        this.attributesLoadFailed.set(false);
        return;
      }

      this.attributesLoaded.set(false);
      this.attributesLoadFailed.set(false);
      // A kit returns the grouped object (own + linked attributes); a regular product an array.
      const attributeRequests = uniqueProductIds.map(productId =>
        isKitByProductId.get(productId)
          ? this.#productsService.getProductAttributesGrouped(productId).pipe(map(response => response.attributes))
          : this.#productsService.getProductAttributes(productId)
      );
      forkJoin(attributeRequests)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: results => {
            if (requestId !== this.attributesRequestId) return;
            const attributesMap = new Map<string, DsmProductAttributeGroup[]>();
            uniqueProductIds.forEach((id, idx) => attributesMap.set(id, results[idx] ?? []));
            this.attributesByProductId.set(attributesMap);
            this.attributesLoaded.set(true);
            this.attributesLoadFailed.set(false);
          },
          error: () => {
            if (requestId !== this.attributesRequestId) return;
            this.attributesByProductId.set(new Map());
            this.attributesLoaded.set(true);
            this.attributesLoadFailed.set(true);
          }
        });
    });
  }

  openPackageDialog(pkg: DsmPackageListItem): void {
    this.activePackageId.set(pkg.id);
  }

  closePackageDialog(): void {
    this.activePackageId.set(null);
    this.editingEntryId.set(null);
  }

  retryPackages(): void {
    this.packagesResource.reload();
  }

  retryPackageDetail(): void {
    this.packageDetailResource.reload();
  }

  retryAttributes(): void {
    this.attributesReloadTick.update(tick => tick + 1);
  }

  removeWorkingEntry(entryId: string): void {
    this.workingEntries.update(entries => entries.filter(entry => entry.id !== entryId));
  }

  confirmEditing(): void {
    this.configFormRef()?.attemptCommit();
  }

  onEntryFormCommit(values: DsmSelectedProductValues): void {
    const id = this.editingEntryId();
    if (!id) return;
    this.workingEntries.update(entries => entries.map(entry => (entry.id === id ? { ...entry, values } : entry)));
    this.editingEntryId.set(null);
  }

  addPackage(): void {
    if (this.packageAddBlocked()) return;
    this.packageEntriesAdded.emit(this.workingEntries());
    this.closePackageDialog();
  }

  isEntryUnconfigurable(entry: DsmSelectedProductEntry): boolean {
    if (!this.attributesLoaded() || this.attributesLoadFailed() || !entry.product.selectionRequired) return false;
    const productId = entry.product.productId;
    if (!productId) return true;
    return (this.attributesByProductId().get(productId) ?? []).length === 0;
  }

  countMissingFields(entry: DsmSelectedProductEntry): number {
    if (!this.attributesLoaded() || !entry.product.selectionRequired) return 0;
    if (this.isEntryUnconfigurable(entry)) return 1;
    const productId = entry.product.productId;
    if (!productId) return 0;
    const attrs = this.attributesByProductId().get(productId) ?? [];
    const selected = entry.values.attributeValues ?? {};
    return attrs.filter(attribute => !selected[attribute.subCategoryAttributeId]).length;
  }

  modelElevationLabel(pkg: DsmPackageListItem): string {
    const model = pkg.modelName ?? '';
    const elev = pkg.elevationCode ?? '';
    if (model && elev) return `${model}/${elev}`;
    return model || elev || '-';
  }

  packageHasDiscount(pkg: DsmPackageListItem): boolean {
    return (pkg.discountPercentage ?? 0) > 0;
  }

  discountedPackageTotal(pkg: DsmPackageListItem): number {
    return (
      pkg.totalPriceAfterDiscount ??
      this.#packageService.amountAfterDiscount(pkg.totalPrice ?? 0, pkg.discountPercentage)
    );
  }

  #buildEntry(
    packageId: string,
    product: DsmPackageProductSummary,
    discountPercentage: number
  ): DsmSelectedProductEntry {
    return {
      id: `pkg-${packageId}-${product.id}`,
      product: this.#toProductOption(product),
      values: {
        locationId: null,
        quantity: (product.quantity || 1) * (product.count || 1),
        unitPrice: product.unitPrice ?? 0,
        notes: '',
        attributeValues: {}
      },
      discountPercentage,
      packageId
    };
  }

  #toProductOption(p: DsmPackageProductSummary): DsmProductOption {
    const product = p.product ?? null;
    const subcategory = product?.subcategory ?? null;
    const uofm = p.uofm ?? product?.uofm ?? null;
    return {
      modelElevationProductsId: p.modelElevationProductsId,
      productId: product?.id,
      code: product?.code ?? '',
      description: product?.description ?? '',
      salesDescription: p.salesDescription || product?.salesDescription || product?.description || '',
      type: p.productType?.name ?? product?.productClass?.name ?? 'BASE',
      uom: uofm?.code ?? uofm?.description ?? '',
      quantity: p.quantity || 1,
      uofmId: uofm?.id ?? '',
      unitPrice: p.unitPrice ?? 0,
      selectionRequired: product?.selectionRequired ?? false,
      category: subcategory?.category?.description ?? subcategory?.category?.code ?? '',
      subcategory: subcategory?.description ?? subcategory?.code ?? '',
      images: product?.images ?? [],
      isKit: product?.productClass?.name === 'Kit',
      linkedProducts: []
    };
  }
}
